import PropTypes from 'prop-types';
import React, { Component } from 'react';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import classnames from 'classnames';

import AcquisitionChannel from './containers/AcquisitionChannel';
import withFetchCustomerResults from '../../../common/hoc/withFetchCustomerResults';
import RoleAwareLink from '../../../common/RoleAwareLink';
import Icon, { GLYPHS } from '../../../common/icon';
import CountryCodePhoneInput from '../../../common/countryCodePhoneInput';
import { maskEmail, maskPhoneNumber } from '../../../common/helpers/mask';

const isPrepaidOnlyCustomer = customer => get(customer, 'blockNoShowFromBookNoPay');

class SelectCustomer extends Component {
  static propTypes = {
    selectedCustomer: PropTypes.object,
    selectCustomer: PropTypes.func.isRequired,
    setCustomerDetails: PropTypes.func.isRequired,
    fetchCustomerResults: PropTypes.func.isRequired,
    clearResults: PropTypes.func.isRequired,
    results: PropTypes.array.isRequired,
    isPaid: PropTypes.bool,
    selectedLeadId: PropTypes.string.isRequired,
    selectLeadId: PropTypes.func.isRequired,
    selectStatus: PropTypes.node,
    canModifyAppointments: PropTypes.bool.isRequired,
    canViewContactInfo: PropTypes.bool.isRequired,
    customerAction: PropTypes.node,
    hideAcquisitionChannel: PropTypes.bool,
  };

  static defaultProps = {
    selectedCustomer: {},
    selectStatus: null,
    isPaid: null,
    customerAction: undefined,
    hideAcquisitionChannel: false,
  };

  state = {
    filterBy: 'name',
    searchText: '',
    phone: '',
    email: '',
  };

  componentWillMount() {
    this.filterCustomers = debounce((field, text) => {
      this.setState({
        filterBy: field,
        searchText: text,
      }, () => this.fetchCustomers());
    }, 200);
    if (this.props.selectedCustomer) this.fillFields(this.props.selectedCustomer);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.selectedCustomer) {
      this.fillFields(nextProps.selectedCustomer);
    }
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(this.props.selectedCustomer, prevProps.selectedCustomer)) {
      this.fillFields(this.props.selectedCustomer);
    }
  }

  componentWillUnmount() {
    this.resetForm();
  }

  onBlur = field => (e) => {
    e.preventDefault();
    this.props.setCustomerDetails(e.target.value, field);
  };

  onCustomerSelected = customer => () => {
    this.setState({
      searchText: '',
    });
    this.props.clearResults();
    this.fillFields(customer);
    const data = pick(customer, [
      'id',
      'name',
      'phone',
      'email',
    ]);
    this.props.selectCustomer(data);
  };

  resetForm = () => {
    this.fillFields(null);
    this.props.clearResults();
  };

  fillFields(customer) {
    const name = isPrepaidOnlyCustomer(customer)
      ? `${get(customer, 'name')} [ONLY PREPAY]` : get(customer, 'name');
    const email = get(customer, 'email') || '';
    const phone = get(customer, 'phone') || '';
    this.updateFields(name, email, phone);

    this.setState({
      phone,
      email,
    });
  }

  updateFields(name = '', email = '', phone = '') {
    this.nameField.value = name;
    this.emailField.value = email;
    this.setState({ phone, email });
  }

  fetchCustomers() {
    this.props.fetchCustomerResults(this.state.searchText);
  }

  inputFilter = (e) => {
    const value = e.target.value.trim();
    const field = e.target.name;
    this.basicInputFilter(field, value);
  };

  onNameChange = (e) => {
    this.inputFilter(e);

    this.props.setCustomerDetails(e.target.value, 'name');
  };

  emailInputFilter = (e) => {
    const value = e.target.value.trim();
    this.setState({ email: value });
    this.basicInputFilter('email', value);
  };

  onEmailChange = (e) => {
    this.emailInputFilter(e);

    this.props.setCustomerDetails(e.target.value, 'email');
  };

  phoneInputFilter = (value) => {
    this.setState({ phone: value });
    this.basicInputFilter('phone', value);
  };

  onPhoneChange = (value) => {
    this.phoneInputFilter(value);

    this.props.setCustomerDetails(value, 'phone');
  };

  basicInputFilter = (field, value) => {
    if (value && value.length > 0) {
      return this.filterCustomers(field, value);
    }

    return this.setState({ customersList: [] });
  };

  cancelSearch = () => {
    if (this.props.selectedCustomer) {
      this.fillFields(this.props.selectedCustomer);
    }
    this.props.clearResults();
  };

  unselectCustomer = () => {
    this.resetForm();
    this.props.selectCustomer(null);
  };

  render() {
    /* eslint-disable jsx-a11y/no-static-element-interactions */
    let customerDropdown;
    let dropdownWrapper;
    const { selectedCustomer } = this.props;
    const { filterBy } = this.state;
    if (this.props.results.length > 0) {
      customerDropdown = this.props.results.map(hit => (
        <div
          className="c-search-dropdown__link"
          onClick={this.onCustomerSelected(hit.document)}
          key={hit.document.id}
        >
          <span className="c-search-dropdown__link-part">{hit.document.name}</span>
          <span className="c-search-dropdown__link-part">
            {!this.props.canViewContactInfo ? maskEmail(hit.document.email) : hit.document.email}
          </span>
          <span className="c-search-dropdown__link-part">
            {!this.props.canViewContactInfo ? maskPhoneNumber(hit.document.phone) : hit.document.phone}
          </span>
        </div>
      ));
      const klass = `c-search-dropdown c-search-dropdown--${filterBy}`;
      dropdownWrapper = (
        <div className={klass}>
          <div className="c-search-dropdown__cancel" onClick={this.cancelSearch}>Cancel</div>
          <div className="c-search-dropdown__box">{customerDropdown}</div>
        </div>
      );
    } else {
      dropdownWrapper = null;
    }

    const disabled = !!(this.props.selectedCustomer || this.props.isPaid || !this.props.canModifyAppointments);

    const customerDeleted = get(this.props, 'selectedCustomer.deleted', false);
    const maskInfo = this.props.selectedCustomer && !this.props.canViewContactInfo;

    return (
      <div className="layout__item u-mb0 u-position-relative">
        <div className="u-mb--">
          <RoleAwareLink
            className="c-label-new"
            to={!customerDeleted && selectedCustomer && `/customers/${get(selectedCustomer, 'id')}`}
          >
            Customer
            {selectedCustomer && customerDeleted && <em className="u-fontsize-tiny"> (Deleted)</em>}
          </RoleAwareLink>
          <button
            type="button"
            className="layout__item c-btn c-btn--blank u-p0 u-width-auto u-ml-- u-align-middle"
            onClick={this.unselectCustomer}
            display-if={this.props.selectedCustomer && !this.props.isPaid && this.props.canModifyAppointments}
          >
            <Icon glyph={GLYPHS.arrowXClose} />
          </button>
          <div display-if={!this.props.hideAcquisitionChannel}>
            <AcquisitionChannel
              leadAutoAssigned={this.props.selectedCustomer && this.props.selectedCustomer.leadAutoAssigned}
              value={this.props.selectedLeadId}
              onChange={this.props.selectLeadId}
              disabled={Boolean(customerDeleted) || !this.props.canModifyAppointments}
            />
          </div>
        </div>
        <div className="u-1/1">
          <div className="c-appointment-details-customer-name-label-wrapper">
            <div className="c-label-new">Name</div>
            {this.props.customerAction}
          </div>
          <input
            onBlur={this.onBlur('name')}
            className={classnames('c-input', {
              'is-prepaid-only-customer': isPrepaidOnlyCustomer(selectedCustomer),
            })}
            type="text"
            name="name"
            placeholder="Search by name"
            ref={(input) => { this.nameField = input; }}
            onChange={this.onNameChange}
            autoComplete="off"
            required
            disabled={disabled}
            data-automation-id="input:name"
          />
        </div>
        <div className="c-label-new u-mt-">Phone</div>
        <CountryCodePhoneInput
          onBlur={this.onBlur('phone')}
          className="c-input"
          type="tel"
          name="phone"
          placeholder="Search by phone"
          ref={(input) => { this.phoneField = input; }}
          onChange={this.onPhoneChange}
          autoComplete="off"
          value={this.state.phone}
          formatter={maskInfo ? maskPhoneNumber : null}
          disabled={disabled}
          /*
            This key instructs React to destroy the component in case current customer changed.
            It's needed as a workaround to bug in react-phone-number-input which is fixed in 2.2.20.
            Unfortunately 2.2.20 doesn't work with current react version.
            Link to issue: https://github.com/catamphetamine/react-phone-number-input/issues/216
           */
          key={this.props.selectedCustomer ? this.props.selectCustomer.id : 'empty'}
        />
        <div className="c-label-new u-mt-">Email</div>
        <input
          onBlur={this.onBlur('email')}
          className="c-input"
          type="email"
          name="email"
          placeholder="Search by email"
          ref={(input) => { this.emailField = input; }}
          onChange={this.onEmailChange}
          autoComplete="off"
          value={maskInfo ? maskEmail(this.state.email) : this.state.email}
          disabled={disabled}
          data-automation-id="input:email"
        />
        {this.props.selectStatus}
        {dropdownWrapper}
      </div>
    );
  }
}

export default withFetchCustomerResults(SelectCustomer);
